import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import { Clear, Edit, Event, OpenInNew, Refresh, Search } from '@mui/icons-material';
import {
	Box,
	Button,
	Autocomplete,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	InputAdornment,
	Link as MuiLink,
	Paper,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid';

import { AgentClient, AgentModel, ConversationClient, ConversationExpirationRequest, ConversationViewModel, TenantUserModel, UserClient } from '@@api';
import { AttributeKeyAutocomplete, formatTimestampFull, useApiClient, useDataGridSearchParams, useIsMobile, useToastService } from '@@common';
import { AttributeCountBadge, BulkSelectionActions, ConfirmDeleteDialog, GalleryHeader, GalleryStatistics, getServerGridRowCount } from '@@components';

const initialPaginationModel: GridPaginationModel = { page: 0, pageSize: 20 };
const initialSortModel: GridSortModel = [{ field: 'created', sort: 'desc' }];
const sortingOrder = ['desc', 'asc'] as const;
const initialFilters = {
	conversationId: '',
	participantTargetId: '',
	attributeKey: '',
	attributeValue: '',
};
const pageSizeOptions = [10, 20, 50, 100];
const expirationPresetDays = [1, 7, 30, 90];
const userSearchPageSize = 50;
const userSearchDebounceMs = 250;
const emptyUsers: TenantUserModel[] = [];
const emptyAgents: AgentModel[] = [];

const sortFieldName: Record<string, string> = {
	name: 'Name',
	created: 'Created',
	updated: 'Updated',
};

function formatParticipantSummary(conversation: ConversationViewModel): string {
	const participants = conversation.participants ?? [];
	if (participants.length === 0) {
		return 'No participants';
	}

	return participants.map((participant) => `${participant.name} (${participant.type})`).join(', ');
}

function getTenantUserName(user: TenantUserModel): string {
	return user.displayName?.trim() || [user.givenName, user.surname].filter(Boolean).join(' ').trim() || user.email;
}

function getTenantUserLabel(user: TenantUserModel): string {
	const name = getTenantUserName(user);
	return name === user.email ? user.email : `${name} (${user.email})`;
}

function getAgentLabel(agent: AgentModel): string {
	return agent.name;
}

function toDateTimeLocalValue(value: string | undefined): string {
	if (!value) {
		return '';
	}

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return '';
	}

	const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
	return localDate.toISOString().slice(0, 16);
}

function getFutureDateTimeLocalValue(daysFromNow: number): string {
	const date = new Date();
	date.setDate(date.getDate() + daysFromNow);
	return toDateTimeLocalValue(date.toISOString());
}

export default function ConversationListView() {
	const client = useApiClient(ConversationClient);
	const userClient = useApiClient(UserClient);
	const agentClient = useApiClient(AgentClient);
	const isMobile = useIsMobile();
	const queryClient = useQueryClient();
	const toastService = useToastService();
	const location = useLocation();
	const { paginationModel, setPaginationModel, sortModel, setSortModel, filters, setFilter, setFilters } = useDataGridSearchParams({
		initialPaginationModel,
		initialSortModel,
		initialFilters,
	});
	const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [expirationDialogMode, setExpirationDialogMode] = useState<'single' | 'bulk'>('single');
	const [expirationDialogConversation, setExpirationDialogConversation] = useState<ConversationViewModel | null>(null);
	const [expirationValue, setExpirationValue] = useState('');
	const [userSearchText, setUserSearchText] = useState('');
	const [debouncedUserSearchText, setDebouncedUserSearchText] = useState('');

	const { conversationId, participantTargetId, attributeKey, attributeValue } = filters;
	const normalizedConversationId = conversationId.trim();
	const normalizedParticipantTargetId = participantTargetId.trim();
	const normalizedAttributeKey = attributeKey.trim();
	const normalizedAttributeValue = attributeValue.trim();
	const conversationDetailSearch = location.search;

	useEffect(() => {
		const timer = window.setTimeout(() => setDebouncedUserSearchText(userSearchText.trim()), userSearchDebounceMs);
		return () => window.clearTimeout(timer);
	}, [userSearchText]);

	const usersQuery = useQuery({
		queryKey: ['users', 'conversation-filter', debouncedUserSearchText],
		queryFn: () => userClient.getUsers(0, userSearchPageSize, debouncedUserSearchText || undefined),
	});
	const userOptions = usersQuery.data?.items ?? emptyUsers;
	const selectedUserFromOptions = userOptions.find((user) => user.id === normalizedParticipantTargetId);
	const agentsQuery = useQuery({
		queryKey: ['agents', 'conversation-filter'],
		queryFn: () => agentClient.getAgents(0, 100, undefined),
		staleTime: 5 * 60 * 1000,
	});
	const agentOptions = agentsQuery.data?.items ?? emptyAgents;
	const selectedAgent = agentOptions.find((agent) => agent.id === normalizedParticipantTargetId) ?? null;
	const selectedUserQuery = useQuery({
		queryKey: ['users', 'conversation-filter', 'selected', normalizedParticipantTargetId],
		queryFn: () => userClient.getUsersByIds({ ids: [normalizedParticipantTargetId] }),
		enabled: !!normalizedParticipantTargetId && !selectedUserFromOptions && !selectedAgent,
	});
	const selectedUser = selectedUserFromOptions ?? selectedUserQuery.data?.[0] ?? null;
	const selectableUsers = useMemo(() => {
		const usersById = new Map(userOptions.map((user) => [user.id, user]));
		if (selectedUser) {
			usersById.set(selectedUser.id, selectedUser);
		}
		return Array.from(usersById.values());
	}, [selectedUser, userOptions]);

	const { data, isLoading, refetch } = useQuery({
		queryKey: [
			'conversations',
			'list',
			paginationModel,
			sortModel,
			normalizedConversationId,
			normalizedParticipantTargetId,
			normalizedAttributeKey,
			normalizedAttributeValue,
		],
		queryFn: () => {
			const sortBy = sortModel.length > 0 ? sortFieldName[sortModel[0].field] : undefined;
			const sortDirection = sortModel.length > 0 ? sortModel[0].sort === 'asc' : undefined;
			return client.search(
				normalizedConversationId || undefined,
				normalizedParticipantTargetId || undefined,
				normalizedAttributeKey || undefined,
				normalizedAttributeValue || undefined,
				paginationModel.page,
				paginationModel.pageSize,
				sortBy,
				sortDirection,
			);
		},
	});

	const rowCount = useMemo(() => getServerGridRowCount(data, paginationModel), [paginationModel, data]);
	const selectedConversationIds = useMemo(() => rowSelectionModel.map((id) => String(id)), [rowSelectionModel]);
	const selectedConversationCount = selectedConversationIds.length;
	const expirationTargetConversationIds =
		expirationDialogMode === 'bulk' ? selectedConversationIds : expirationDialogConversation ? [expirationDialogConversation.id] : [];
	const expirationTargetCount = expirationTargetConversationIds.length;
	const expirationDialogOpen = expirationDialogMode === 'bulk' || expirationDialogConversation != null;

	const deleteMutation = useMutation({
		mutationFn: async () => {
			await Promise.all(selectedConversationIds.map((id) => client.delete(id)));
		},
		onSuccess: async () => {
			const deletedCount = selectedConversationIds.length;
			toastService.success(`${deletedCount} ${deletedCount === 1 ? 'conversation' : 'conversations'} deleted.`);
			setDeleteDialogOpen(false);
			setRowSelectionModel([]);
			await queryClient.invalidateQueries({ queryKey: ['conversations', 'list'] });
		},
		onError: (error) => toastService.error(error instanceof Error ? error.message : 'Failed to delete selected conversations.'),
	});

	const expirationMutation = useMutation({
		mutationFn: async (request: { conversationIds: string[]; body: ConversationExpirationRequest }) => {
			await Promise.all(request.conversationIds.map((conversationId) => client.setExpiry(conversationId, request.body)));
		},
		onSuccess: async (_, request) => {
			const updatedCount = request.conversationIds.length;
			const conversationLabel = updatedCount === 1 ? 'conversation' : 'conversations';
			toastService.success(
				request.body.clearExpiration ? `${updatedCount} ${conversationLabel} expiration cleared.` : `${updatedCount} ${conversationLabel} expiration updated.`,
			);
			setExpirationDialogConversation(null);
			setExpirationDialogMode('single');
			setExpirationValue('');
			await queryClient.invalidateQueries({ queryKey: ['conversations', 'list'] });
		},
		onError: (error) => toastService.error(error instanceof Error ? error.message : 'Failed to update conversation expiration.'),
	});

	const clearFilters = () => {
		setFilters(initialFilters, true);
		setUserSearchText('');
	};

	const openExpirationDialog = useCallback((conversation: ConversationViewModel) => {
		setExpirationDialogMode('single');
		setExpirationDialogConversation(conversation);
		setExpirationValue(toDateTimeLocalValue(conversation.expiresAt));
	}, []);

	const openBulkExpirationDialog = () => {
		setExpirationDialogMode('bulk');
		setExpirationDialogConversation(null);
		setExpirationValue('');
	};

	const closeExpirationDialog = () => {
		setExpirationDialogConversation(null);
		setExpirationDialogMode('single');
		setExpirationValue('');
	};

	const saveExpiration = () => {
		if (expirationTargetCount === 0 || !expirationValue) {
			return;
		}

		expirationMutation.mutate({
			conversationIds: expirationTargetConversationIds,
			body: {
				expiresAt: new Date(expirationValue).toISOString(),
				clearExpiration: false,
			},
		});
	};

	const clearExpiration = () => {
		if (expirationTargetCount === 0) {
			return;
		}

		expirationMutation.mutate({
			conversationIds: expirationTargetConversationIds,
			body: {
				clearExpiration: true,
			},
		});
	};

	const columns = useMemo<GridColDef<ConversationViewModel>[]>(
		() => [
			{
				field: 'name',
				headerName: 'Name',
				flex: 1,
				minWidth: isMobile ? 120 : 220,
				renderCell: (params) => (
					<MuiLink
						component={RouterLink}
						to={{ pathname: `/conversation/${params.row.id}`, search: conversationDetailSearch }}
						sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
					>
						{params.row.name}
					</MuiLink>
				),
			},
			{
				field: 'participants',
				headerName: 'Participants',
				flex: 1.2,
				minWidth: 260,
				sortable: false,
				valueGetter: (_value, row) => formatParticipantSummary(row),
			},
			{
				field: 'attributes',
				headerName: 'Attributes',
				width: 110,
				sortable: false,
				align: 'center',
				headerAlign: 'center',
				renderCell: (params) => <AttributeCountBadge attributes={params.row.attributes} />,
			},
			{
				field: 'created',
				headerName: 'Created',
				width: 190,
				valueFormatter: (value) => (value ? formatTimestampFull(value as string) : ''),
			},
			{
				field: 'updated',
				headerName: 'Updated',
				width: 190,
				valueFormatter: (value) => (value ? formatTimestampFull(value as string) : ''),
			},
			{
				field: 'expiresAt',
				headerName: 'Expires On',
				sortable: false,
				width: 220,
				renderCell: (params) => (
					<Stack direction="row" spacing={1} alignItems="center" sx={{ height: '100%', minWidth: 0 }}>
						<Typography variant="body2" noWrap title={params.row.expiresAt ? formatTimestampFull(params.row.expiresAt) : 'No expiration'} sx={{ minWidth: 0 }}>
							{params.row.expiresAt ? formatTimestampFull(params.row.expiresAt) : 'No expiration'}
						</Typography>
						<IconButton size="small" aria-label="Edit expiration" onClick={() => openExpirationDialog(params.row)}>
							<Edit fontSize="small" />
						</IconButton>
					</Stack>
				),
			},
			{
				field: 'open',
				headerName: '',
				width: 64,
				sortable: false,
				filterable: false,
				align: 'center',
				renderCell: (params) => (
					<IconButton
						component={RouterLink}
						to={{ pathname: `/conversation/${params.row.id}`, search: conversationDetailSearch }}
						size="small"
						aria-label="Open conversation"
					>
						<OpenInNew fontSize="small" />
					</IconButton>
				),
			},
		],
		[conversationDetailSearch, isMobile, openExpirationDialog],
	);

	const conversations = data?.items ?? [];
	const conversationsWithExpiry = conversations.filter((conversation) => !!conversation.expiresAt).length;

	return (
		<Stack direction="column" spacing={2} sx={{ flexGrow: 1, flexShrink: 1, minWidth: 0, minHeight: 0, p: (theme) => `${theme.layout.edgeGap}px`, overflow: 'hidden' }}>
			<GalleryHeader
				title="Conversations"
				subtitle="Review and manage conversations across your workspace."
				utilityActions={<IconButton aria-label="Refresh conversations" title="Refresh conversations" onClick={() => refetch()}><Refresh /></IconButton>}
				statistics={<GalleryStatistics items={[{ label: 'Conversations on this page', value: conversations.length }, { label: 'With expiration', value: conversationsWithExpiry }, { label: 'Selected', value: selectedConversationCount }]} />}
				search={
					<TextField
						size="small"
						placeholder="Search conversation ID…"
						inputProps={{ 'aria-label': 'Search conversation ID' }}
						value={conversationId}
						onChange={(event) => setFilter('conversationId', event.target.value, true)}
						fullWidth
						InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>, endAdornment: conversationId ? <InputAdornment position="end"><IconButton aria-label="Clear conversation ID search" size="small" onClick={() => setFilter('conversationId', '', true)}><Clear fontSize="small" /></IconButton></InputAdornment> : undefined }}
					/>
				}
			/>

			<Paper variant="outlined" sx={{ px: { xs: 1, sm: 2 }, py: 1.5 }}>
				<Stack
				direction={{ xs: 'column', sm: 'row' }}
				spacing={1}
				alignItems={{ xs: 'stretch', sm: 'center' }}
				sx={{ px: { xs: 1, sm: 2 }, flexWrap: 'wrap', rowGap: 1 }}
			>
				<Autocomplete
					options={selectableUsers}
					value={selectedUser}
					onChange={(_, user) => setFilter('participantTargetId', user?.id ?? '', true)}
					onInputChange={(_, value, reason) => {
						if (reason === 'input' || reason === 'clear') {
							setUserSearchText(value);
						}
					}}
					loading={usersQuery.isLoading || selectedUserQuery.isLoading}
					filterOptions={(options) => options}
					getOptionLabel={getTenantUserLabel}
					isOptionEqualToValue={(option, value) => option.id === value.id}
					noOptionsText="No matching users"
					sx={{ width: { xs: '100%', sm: 300 } }}
					renderInput={(params) => (
						<TextField
							{...params}
							size="small"
							label="User"
							placeholder="Search users"
							InputProps={{
								...params.InputProps,
								endAdornment: (
									<>
										{usersQuery.isFetching || selectedUserQuery.isFetching ? <CircularProgress size={18} /> : null}
										{params.InputProps.endAdornment}
									</>
								),
							}}
						/>
					)}
				/>
				<Autocomplete
					options={agentOptions}
					value={selectedAgent}
					onChange={(_, agent) => setFilter('participantTargetId', agent?.id ?? '', true)}
					loading={agentsQuery.isLoading}
					getOptionLabel={getAgentLabel}
					isOptionEqualToValue={(option, value) => option.id === value.id}
					noOptionsText="No matching agents"
					sx={{ width: { xs: '100%', sm: 260 } }}
					renderOption={(props, agent) => (
						<li {...props} key={agent.id}>
							<Box>
								<Typography variant="body2">{agent.name}</Typography>
								<Typography variant="caption" color="text.secondary">
									{agent.name}
								</Typography>
							</Box>
						</li>
					)}
					renderInput={(params) => (
						<TextField
							{...params}
							size="small"
							label="Agent"
							placeholder="Search agents"
							InputProps={{
								...params.InputProps,
								endAdornment: (
									<>
										{agentsQuery.isFetching ? <CircularProgress size={18} /> : null}
										{params.InputProps.endAdornment}
									</>
								),
							}}
						/>
					)}
				/>
				<AttributeKeyAutocomplete
					label="Attribute"
					value={attributeKey}
					onChange={(value) => setFilter('attributeKey', value, true)}
					sx={{ width: { xs: '100%', sm: 220 } }}
				/>
				<TextField
					size="small"
					label="Value"
					value={attributeValue}
					onChange={(event) => {
						setFilter('attributeValue', event.target.value, true);
					}}
					sx={{ width: { xs: '100%', sm: 220 } }}
				/>
				<IconButton aria-label="Clear filters" title="Clear filters" onClick={clearFilters}>
					<Clear />
				</IconButton>
				<Box sx={{ flex: '1 1 auto', minWidth: 24 }} />
				<BulkSelectionActions
					selectedCount={selectedConversationCount}
					onClearSelection={() => setRowSelectionModel([])}
					onDeleteSelected={() => setDeleteDialogOpen(true)}
					deleteLabel="Delete selected conversations"
					deleteDisabled={deleteMutation.isPending}
				>
					<IconButton
						size="small"
						aria-label="Set expiration for selected conversations"
						title="Set expiration for selected conversations"
						onClick={openBulkExpirationDialog}
						disabled={expirationMutation.isPending || selectedConversationCount === 0}
					>
						<Event fontSize="small" />
					</IconButton>
				</BulkSelectionActions>
				</Stack>
			</Paper>

			<Paper sx={{ flexGrow: 1, flexShrink: 1, minWidth: 0, overflow: 'hidden' }}>
				<DataGrid
					loading={isLoading}
					rows={data?.items ?? []}
					rowCount={rowCount}
					columns={columns}
					paginationMode="server"
					sortingMode="server"
					disableColumnFilter
					pageSizeOptions={pageSizeOptions}
					paginationModel={paginationModel}
					onPaginationModelChange={setPaginationModel}
					sortModel={sortModel}
					onSortModelChange={setSortModel}
					sortingOrder={sortingOrder}
					checkboxSelection
					disableRowSelectionOnClick
					rowSelectionModel={rowSelectionModel}
					onRowSelectionModelChange={setRowSelectionModel}
					columnVisibilityModel={{
						participants: !isMobile,
						attributes: !isMobile,
						created: !isMobile,
						updated: !isMobile,
						expiresAt: !isMobile,
					}}
					density={isMobile ? 'compact' : 'standard'}
					sx={{ border: 0, minWidth: 0 }}
					rowHeight={isMobile ? 48 : 56}
				/>
			</Paper>

			<ConfirmDeleteDialog
				open={deleteDialogOpen}
				title="Delete Conversations"
				resourceName="conversation"
				selectedCount={selectedConversationCount}
				isDeleting={deleteMutation.isPending}
				onClose={() => setDeleteDialogOpen(false)}
				onConfirm={() => deleteMutation.mutate()}
			/>

			<Dialog open={expirationDialogOpen} onClose={closeExpirationDialog} fullWidth maxWidth="xs">
				<DialogTitle>{expirationDialogMode === 'bulk' ? 'Bulk Conversation Expiration' : 'Conversation Expiration'}</DialogTitle>
				<DialogContent>
					<Stack spacing={2} sx={{ pt: 1 }}>
						<Typography variant="body2" color="text.secondary" noWrap title={expirationDialogMode === 'bulk' ? undefined : expirationDialogConversation?.name}>
							{expirationDialogMode === 'bulk'
								? `${selectedConversationCount} selected ${selectedConversationCount === 1 ? 'conversation' : 'conversations'}`
								: expirationDialogConversation?.name}
						</Typography>
						<Stack spacing={1}>
							<Typography variant="subtitle2">Expire From Now</Typography>
							<Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
								{expirationPresetDays.map((days) => (
									<Button key={days} size="small" variant="outlined" onClick={() => setExpirationValue(getFutureDateTimeLocalValue(days))}>
										{days} {days === 1 ? 'day' : 'days'}
									</Button>
								))}
							</Stack>
						</Stack>
						<TextField
							size="small"
							label="Expires On"
							type="datetime-local"
							value={expirationValue}
							onChange={(event) => setExpirationValue(event.target.value)}
							InputLabelProps={{ shrink: true }}
							fullWidth
						/>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={closeExpirationDialog} disabled={expirationMutation.isPending}>
						Cancel
					</Button>
					<Button
						color="inherit"
						onClick={clearExpiration}
						disabled={
							expirationMutation.isPending || expirationTargetCount === 0 || (expirationDialogMode === 'single' && !expirationDialogConversation?.expiresAt)
						}
					>
						Clear Expiration
					</Button>
					<Button variant="contained" onClick={saveExpiration} disabled={expirationMutation.isPending || expirationTargetCount === 0 || !expirationValue}>
						Save
					</Button>
				</DialogActions>
			</Dialog>
		</Stack>
	);
}
